import {
  WORK_INFORMATION_ITEMS,
  CHANGE_QUESTIONS,
} from 'AppSrc/core/components/forms/form-types/create-crq/form-definition/section-constants';
import {
  ATTACHED_FILE,
  SUMMARY,
  WORK_INFORMATION_ITEMS_FIELD_ID,
  WORK_INFORMATION_TYPE,
  NETWORK_IT_SECURITY_ASSESSMENT_NOT_REQUIRED,
  CANCELLATION_INFORMATION,
  PEER_REJECTED_TYPE,
  PEER_APPROVED_TYPE,
  PEER_GROUP_REVIEW_TYPE_MOBILE,
  PEER_GROUP_REVIEW_SUMMARY,
  mobileVINERefWIT,
} from 'AppSrc/core/components/forms/form-types/create-crq/section-five/work-information-items/work-information-item-constants';
import {
  MOBILE,
  FIXED,
  BUSINESS_JUSTIFICATION,
  BUSINESS_JUSTIFICATION_FIXED,
  preImp6No,
  preImp2No,
  SERVICE_IMPACT_ASSESSMENT_FIXED,
} from 'AppSrc/core/components/forms/value-dependency-constants';
import { fileDb } from 'AppSrc/core/utility-storage/file-db';
import { store } from 'AppSrc/core/store/store';
import { workInfoItemAttachmentRead } from 'AppSrc/core/work-info-items/work-info-item-actions';
import {
  getBizJustQABase,
  bizJustQAJoiner,
} from 'AppSrc/core/components/forms/form-types/create-crq/section-two/business-justification-constants';
import {
  SUBMIT_TO_APPROVALS,
  REJECT_FROM_PEER_REVIEW,
  CANCEL_CRQ,
  REVOKE_APPROVAL,
} from 'AppSrc/core/crq/crq-status-transitions';
import {
  nowInDefaultTZ,
  nowInNetworkTZ,
} from 'AppSrc/core/utility-functions/date-functions';
import { getPreImp6Value, getPreImp2Value } from 'AppSrc/core/components/forms/form-types/create-crq/common-field-selectors';
import { whyNoNetworkItSecurityApprovalId } from 'AppSrc/core/components/forms/form-types/create-crq/section-two/section-two-constants';
import { COORDINATOR } from 'AppSrc/core/components/forms/form-base-components/data-structure/constants';
import {
  getFormDataFieldValue,
  getFieldValueForParameters,
  currentSelectedNetwork,
  isCrqExpedited,
  getIsOlaCrq,
  isCrqEmergency,
} from './common-logic';
import { businessJustificationA } from './section-two/business-justification-a';
import { businessJustificationB } from './section-two/business-justification-b';
import { businessJustificationC } from './section-two/business-justification-c';

// constants work information item types
// const BUSINESS_JUSTIFICATION_TYPE = BUSINESS_JUSTIFICATION;

// constants work information item text
const BUSINESS_JUSTIFICATION_SUMMARY = 'Expedited - Business Critical Justification';


const DEFAULT_SOURCE = {
  [FIXED]: '9000',
  [MOBILE]: 'Other',
};

// FN: 01/06/2020
// This is pretty much so syntactic sugar to get the work info items out of section 5.
// The reason why we do this is all work information items from the server are stored in
// section 5 as form data.
const getCachedWIIs = (formData) => getFormDataFieldValue({
  formData,
  sectionId: WORK_INFORMATION_ITEMS,
  fieldId: WORK_INFORMATION_ITEMS_FIELD_ID,
});

const getExistingIdsForType = ({
  cachedWIIs,
  wiType,
}) => {
  if (!cachedWIIs) {
    // DVB removing this error. It's perfectly reasonable for cachedWIIs to be null when raising a CRQ
    // as a coordinator. The CRQ is saved before the wii section
    // console.error(`Unable to find find filterWIIForWiiType as cachedWIIs is defined as: ${cachedWIIs}`);
    return [];
  }

  const sectionIds = cachedWIIs.formSectionsByDisplayOrder;

  return sectionIds.filter((sectionId) => {
    const foundWiType = getFieldValueForParameters({
      formData: cachedWIIs,
      sectionId,
      fieldId: WORK_INFORMATION_TYPE,
    });
    return wiType === foundWiType;
  });
};

const filterWIIForWiiType = (params = {}) => {
  const {
    cachedWIIs,
    wiType,
  } = params;
  const matchedIds = getExistingIdsForType({
    cachedWIIs,
    wiType,
  });
  const matchedWIIs = matchedIds.map((matchedId) => cachedWIIs.dataByFormSectionId[matchedId]);

  return matchedWIIs;
};


const getUniqueWIIID = (params = {}) => {
  const {
    cachedWIIs,
    wiType,
  } = params;

  // gets a list of work info items that match the provided wiType
  const matchedTypeWiis = filterWIIForWiiType({
    cachedWIIs,
    wiType,
  });

  const workInfoItemId = matchedTypeWiis[0] ? matchedTypeWiis[0].workInfoItemId : null;

  if (matchedTypeWiis.length > 1) {
    console.warn(`Found multiple work information items with unique wiType: ${wiType}.
    As such we are only updating workInfoItemId: ${workInfoItemId} of workInfoItemIds: ${matchedTypeWiis.map((wii) => wii.workInfoItemId).join(', ')}`);
  }


  return workInfoItemId;
};



// create parameters shape for a work info item, with null attachment
const getWIIparams = ({
  workInfoItemId,
  network,
  crqId,
  summary,
  wiType,
  notes,
  locked,
  viewAccess,
}) => {

  const source = DEFAULT_SOURCE[network];
  return ({
    id: workInfoItemId,
    network,
    crq_id: crqId,
    summary,
    notes: notes || '',
    source,
    info_type: wiType,
    attachment: null,
    locked,
    view_access: viewAccess,
  });
};

// get params for a WII with an attachment
// returns a promise as the file data needs to be read
const getWIIWithAttachmentParams = ({
  workInfoItemId,
  network,
  crqId,
  summary,
  wiType,
  fileId,
  fileName,
  locked,
  viewAccess,
  notes,
}) => {
  const params = getWIIparams({
    workInfoItemId,
    network,
    crqId,
    summary,
    wiType,
    locked,
    viewAccess,
    notes,
  });
  const file = fileDb.get(fileId);
  if (!file) {
    // If there is a value for filename, this is an existing WII with attachment that has
    // already been saved. We send an attachment to the server with the filename set and data = null
    // otherwise there is no attachment the value should be null.
    const attachment = fileName ? ({
      name: fileName,
      data: null,
    }) : null;
    // we can immediately return the data with using promise.resolve
    return Promise.resolve({
      ...params,
      attachment,
    });
  }
  const reader = new window.FileReader();
  // construct a promise that will resolve once the file data is read
  return new Promise((resolve, reject) => {
    try {
      reader.onloadend = () => {
        const base64Data = reader.result.replace(/^data:.+;base64,/, '');
        // let redux know we've processed the file, for the loader progress
        store.dispatch(workInfoItemAttachmentRead(fileId));
        resolve({
          ...params,
          attachment: {
            name: fileName,
            data: base64Data,
          },
        });
      };
      reader.readAsDataURL(file.file);
    }
    catch (error) {
      reject(error);
    }
  });
};

const getParamsFromSection5 = ({
  modalData,
  crqId,
  network,
}) => {
  if (!modalData) { return Promise.resolve([]); }
  const sectionIds = modalData.formSectionsByDisplayOrder;

  // for each section call getWIIWithAttachmentParams and collect the promises
  const promises = sectionIds.map((sectionId) => {
    // This function follow new approches to change summary input to notes field and keep old code format
    // to all work info type section 5 mapping we have mapped through some infotype
    const wiType = getFieldValueForParameters({
      formData: modalData,
      sectionId,
      fieldId: WORK_INFORMATION_TYPE,
    });

    // get the work info id out of the modal data
    const sectionData = modalData.dataByFormSectionId[sectionId];
    const workInfoItemId = sectionData ? sectionData.workInfoItemId : null;
    const locked = sectionData ? sectionData.locked : null;
    const viewAccess = sectionData ? sectionData.viewAccess : null;
    // const notes = sectionData ? sectionData.notes : null;
    let summary = '';
    let notes = '';
    const currentStatus = window.sessionStorage.getItem('status');
    const currentStatusreason = window.sessionStorage.getItem('statusReason');
    // Since WII summary field is mandatory in remedy therefore We are sending dummy text string.
    // const summary = 'default text for summary';
    // Since Notes has higher text limit we are remapping the WII summary to notes.
    // const summary = getFieldValueForParameters({
    // // let notes = getFieldValueForParameters({
    //   formData: modalData,
    //   sectionId,
    //   fieldId: SUMMARY,
    // });
    const hasSIAAttachKeyword = sectionData.dataByFieldId.summary.value === '#ImportSIA#' ? true : false;

    // Since we have various wii which needed diff mapping we have diff diff if-else condition not swap between notes to summary or summary to notes
    if (wiType === '20500' || wiType === '20600' || wiType === '23340' || wiType === 'Peer Approved' || wiType === '23350' || wiType === '29000' || (sectionData.workInfoItemId === undefined && wiType === '9000' && currentStatus === '4' && currentStatusreason === '10500') || ((hasSIAAttachKeyword === true) && wiType === '9000')) {
      // summary = getFieldValueForParameters({
      //   formData: modalData,
      //   sectionId,
      //   fieldId: SUMMARY,
      // });
      // notes = sectionData ? sectionData.notes : null;

      // This mapping Attach SIA Import at circuit check stage for 9000
      if ((sectionData.workInfoItemId === undefined && wiType === '9000')) {
        window.sessionStorage.setItem('siaAddedAtCircuitCheck', true);
        const attcheRemedy = '#ImportSIA#';

        // To attach #keyword sia import at circuit check stage
        notes = getFieldValueForParameters({
          formData: modalData,
          sectionId,
          fieldId: SUMMARY,
        });
        // Since Notes has higher text limit we are remapping the WII summary to notes.
        if (notes === null || notes === 'default text for summary') {
          notes = modalData.dataByFormSectionId[sectionId].notes;
        }
        summary = `${attcheRemedy}${summary}`;
      }
      // This mapping for other wii like general info, peer approve, reject etc
      else if (wiType !== '9000' || hasSIAAttachKeyword) {
        summary = getFieldValueForParameters({
          formData: modalData,
          sectionId,
          fieldId: SUMMARY,
        });
        notes = sectionData ? sectionData.notes : null;
      }
    }
    // This Mapping for all File section 5 normal Wii
    else {
      notes = getFieldValueForParameters({
        formData: modalData,
        sectionId,
        fieldId: SUMMARY,
      });
      // Since Notes has higher text limit we are remapping the WII summary to notes.
      if (notes === null || notes === 'default text for summary') {
        notes = modalData.dataByFormSectionId[sectionId].notes;
      }
      summary = 'default text for summary';
    }

    const attachedFile = getFieldValueForParameters({
      formData: modalData,
      sectionId,
      fieldId: ATTACHED_FILE,
    });

    const fileId = attachedFile ? attachedFile.fileId : null;
    const fileName = attachedFile ? attachedFile.fileName : null;

    return getWIIWithAttachmentParams({
      workInfoItemId,
      network,
      crqId,
      summary,
      wiType,
      fileId,
      fileName,
      locked,
      viewAccess,
      notes,
    });
  });
  // return a promise when all the others have resolved
  return Promise.all(promises);
};

const getParamsForBusinessJustification = ({
  crqData,
  crqId,
  network,
  roles,
}) => {
  const isExpedited = isCrqExpedited(crqData);
  const isMobile = network === MOBILE;
  const isFixed = network === FIXED;
  const isMobileCoordinator = roles[MOBILE] === COORDINATOR;
  const isFixedCoordinator = roles[FIXED] === COORDINATOR;
  const shouldCreateWii = isExpedited && (isMobile || isFixed) && (isMobileCoordinator || isFixedCoordinator);
  if (!shouldCreateWii) {
    return null;
  }


  const businessFields = [
    businessJustificationA,
    businessJustificationB,
    businessJustificationC,
  ];
  // notes text should be each question followed by each answer on a new line
  // with a single new line between
  const notes = businessFields.map((fieldData, index) => {
    const num = index + 1;
    const fieldId = fieldData.id;
    const question = fieldData.vFParams.fieldTitle;
    const answer = getFormDataFieldValue({
      formData: crqData,
      sectionId: CHANGE_QUESTIONS,
      fieldId,
    });
    const bizJustQABase = getBizJustQABase(question, num);
    return `${bizJustQABase}${answer}`;
  }).join(bizJustQAJoiner);


  // constants work information item types
  let BUSINESS_JUSTIFICATION_TYPE;
  if (network === MOBILE) {
    BUSINESS_JUSTIFICATION_TYPE = BUSINESS_JUSTIFICATION;
  }
  if (network === FIXED) {
    BUSINESS_JUSTIFICATION_TYPE = BUSINESS_JUSTIFICATION_FIXED;
  }
  const cachedWIIs = getCachedWIIs(crqData);
  const wiType = BUSINESS_JUSTIFICATION_TYPE;

  const workInfoItemId = getUniqueWIIID({
    cachedWIIs,
    wiType,
  });


  return getWIIparams({
    workInfoItemId,
    crqId,
    network,
    notes,
    summary: BUSINESS_JUSTIFICATION_SUMMARY,
    wiType,
  });
};



// All new mobile CRQs should have a WII of type 'Peer Group Review'
const getParamsForPeerGroupReviewParams = ({
  crqId,
  network,
  formData,
}) => {
  // only generate for mobile CRQs
  if (network !== MOBILE) return null;
  const wiType = PEER_GROUP_REVIEW_TYPE_MOBILE;
  const summary = PEER_GROUP_REVIEW_SUMMARY;

  const cachedWIIs = getCachedWIIs(formData);
  // we should only add this WII if it doesn't already exist
  // gets a list of work info items that match the provided wiType
  const existingIds = getExistingIdsForType({
    cachedWIIs,
    wiType,
  });
  if (existingIds.length > 0) {
    // there is an existing wii of this type, we shouldn't create one with the
    // default summary as this may have been added outside of the portal
    return null;
  }

  // generate a new WII for PeerGroupReview
  return getWIIparams({
    workInfoItemId: null,
    crqId,
    network,
    notes: '',
    summary,
    wiType,
  });
};




const getParamsForNetowrkItSecurityAssessment = ({
  formData,
  crqId,
  network,
}) => {

  const correctNetwork = network === FIXED;

  // should only happen on fixed
  if (!correctNetwork) {
    return null;
  }

  const preImp6Value = getPreImp6Value(formData);

  const correctState = preImp6Value === preImp6No;

  // should only happen if preImp6 is answored with no
  if (!correctState) {
    return null;
  }

  const summary = getFormDataFieldValue({
    formData,
    sectionId: CHANGE_QUESTIONS,
    fieldId: whyNoNetworkItSecurityApprovalId,
  });

  const cachedWIIs = getCachedWIIs(formData);
  const wiType = NETWORK_IT_SECURITY_ASSESSMENT_NOT_REQUIRED[network];

  const workInfoItemId = getUniqueWIIID({
    cachedWIIs,
    wiType,
  });

  return getWIIparams({
    workInfoItemId,
    crqId,
    network,
    notes: '',
    summary,
    wiType,
  });
};

// SIA WII call when selecting customer impacting question 'Yes' in Fixed CRQ.
const getParamsForCustomerImpacting = ({
  formData,
  crqId,
  network,
}) => {

  const isOloCrq = getIsOlaCrq(formData);
  const correctNetwork = network === FIXED;

  // should only happen on fixed
  if (!correctNetwork) {
    return null;
  }

  const preImp2Value = getPreImp2Value(formData);

  const correctState = preImp2Value;

  // should only happen if preImp2 is answored with no )
  if ((correctState === preImp2No) || (correctNetwork === isOloCrq)) {
    return null;
  }

  const cachedWIIs = getCachedWIIs(formData);
  const wiType = SERVICE_IMPACT_ASSESSMENT_FIXED;

  const workInfoItemId = getUniqueWIIID({
    cachedWIIs,
    wiType,
  });

  return getWIIparams({
    workInfoItemId,
    crqId,
    network,
    notes: '',
    summary: 'customer impacting',
    wiType,
  });
};

// This is for TechnologyRecoveryPlan logic
const getParamsForTechnologyRecoveryPlan = ({
  formData,
  crqId,
  network,
}) => {

  const isOloCrq = getIsOlaCrq(formData);
  const technologyPlanRequired = 'technology-plan-required';
  const technologyRecoveryResponse = network === MOBILE ? formData.dataByFormSectionId[CHANGE_QUESTIONS].dataByFieldId[technologyPlanRequired].displayValues[0].value : null;

  const technologyPlanRequiredFixed = 'technology-plan-required-fixed';
  const technologyRecoveryResponseFixed = (network === FIXED && isOloCrq === false) ? formData.dataByFormSectionId[CHANGE_QUESTIONS].dataByFieldId[technologyPlanRequiredFixed].value[0] : null;

  const correctNetwork = ((network === MOBILE && technologyRecoveryResponse === 'No') || ((network === FIXED && technologyRecoveryResponseFixed === 'No')));
  // should only happen on fixed
  if (!correctNetwork) {
    return null;
  }

  const cachedWIIs = getCachedWIIs(formData);
  const wiType = network === FIXED ? '29000' : 'General Information';

  const workInfoItemId = getUniqueWIIID({
    cachedWIIs,
    wiType,
  });

  const technologyPlanExclusion = 'technology-plan-exclusion';

  const notesValue = network === MOBILE ? formData.dataByFormSectionId[CHANGE_QUESTIONS].dataByFieldId[technologyPlanExclusion].displayValues : null;

  const technologyPlanExclusionFixed = 'technology-plan-exclusion-fixed';

  const notesValueFixed = network === FIXED ? formData.dataByFormSectionId[CHANGE_QUESTIONS].dataByFieldId[technologyPlanExclusionFixed].displayValues : null;

  return getWIIparams({
    workInfoItemId,
    crqId,
    network,
    notes: network === FIXED ? notesValueFixed : notesValue,
    summary: 'Technology Recovery Plan',
    wiType,
  });
};


// This is for Mobile VINE aotomate WII logic
const getParamsForMobileVINE = ({
  vineInfoType,
  // formData,
  crqId,
  network,
  riskLevel,
  changeReasone,
}) => {


  const TEST_RESULTS_DETAILS = 'Test Results - Details';
  const RISK_STATEMENT = 'Risk Statement';
  const CHANGE_JUSTIFICTATION = 'Change Justification';
  const INSTALL_PLAN_MOBILE = 'Install Plan';


  const mobileVINENotes = {
    [TEST_RESULTS_DETAILS]: '_ACE_For VINE Change - Not Applicable',
    [RISK_STATEMENT]: `_ACE_Fixed CRQ Risk Level - ${riskLevel ? riskLevel : ''}`,
    [CHANGE_JUSTIFICTATION]: `_ACE_Fixed CRQs Change Reason - ${changeReasone ? changeReasone : ''}`,
    [INSTALL_PLAN_MOBILE]: '_ACE_For VINE Change - Not Applicable',
  };

  const notesValue = vineInfoType ? mobileVINENotes[vineInfoType] : 'dummy text vine';

  // should only happen on fixed
  if (!network) {
    return null;
  }

  // const cachedWIIs = getCachedWIIs(formData);
  const wiType = vineInfoType;

  const workInfoItemId = getUniqueWIIID({
    // cachedWIIs,
    wiType,
  });


  return getWIIparams({
    workInfoItemId,
    crqId,
    network,
    notes: notesValue,
    summary: 'default text for summary',
    wiType,
  });
};




const getApproverHistoryNotes = (params = {}) => {
  const {
    crqData,
    loggedInUserName,
    status, // not to be confused with CRQ statuses, this is a approver history status such as Approved/Rejected/Pending
    peerRejectionReason,
  } = params;

  const {
    peerReviewerId,
    peerReviewerName,
    peerReviewerGroup,
  } = crqData;

  const fallBack = '-';
  // TIL is not correctly returning peer_reviewer_name for crqs. So that the approval history
  // makes sense, we are instead using the logged in users name at the time of rejection or approval.
  // NOTE: this will lead to strange behaviour when the dev tools are used as the logged in user is not the
  // peer reviewer assigned to the CRQ. However this is a minor issue only on dev.
  const peer = peerReviewerName ? peerReviewerName : loggedInUserName;

  if (!status) {
    console.error('Please provide a status for getApproverHistorySummary such as Approved, Rejected or Pending');
  }


  const date = nowInDefaultTZ().format('DD/MM/YYYY');
  const notesData = {
    date,
    type: 'Peer',
    group: peerReviewerGroup ? peerReviewerGroup : fallBack,
    peer: peer ? peer : fallBack,
    // although not displayed on the front end, it was felt it would be good to keep a record
    // of the peer reviewer id, as name is not populated by TIL
    peerId: peerReviewerId ? peerReviewerId : fallBack,
    status: status ? status : fallBack,
    comment: peerRejectionReason,
  };
  return JSON.stringify(notesData);

};


// WII parameters based on the state transition
// for example, adding a WII on peer review approval
const getParamsForStatusTransition = ({
  statusTransition,
  crqData,
  crqId,
  network,
  user,
}) => {
  if (!statusTransition) { return null; }
  const {
    id: transitionId,
  } = statusTransition;
  const isEmergency = isCrqEmergency(crqData);
  const loggedInUserName = `${user.firstName} ${user.lastName}`;
  // const isEmergency = true; // isCrqEmergency(formData);
  if ((transitionId === SUBMIT_TO_APPROVALS)) {



    const notes = getApproverHistoryNotes({
      crqData,
      loggedInUserName,
      status: 'Approved',
    });

    const wiType = PEER_APPROVED_TYPE[network];
    const workInfoItemId = null; // We never update this, we create new ones
    return getWIIparams({
      workInfoItemId,
      crqId,
      network,
      notes,
      summary: 'Approved by peer',
      wiType,
    });
  }

  if (transitionId === REJECT_FROM_PEER_REVIEW) {

    const {
      peerRejectionReason,
    } = crqData;

    const wiType = PEER_REJECTED_TYPE[network];
    const workInfoItemId = null; // We never update this, we create new ones

    const notes = getApproverHistoryNotes({
      crqData,
      loggedInUserName,
      status: 'Rejected',
      peerRejectionReason,
    });

    return getWIIparams({
      workInfoItemId,
      crqId,
      network,
      notes,
      summary: 'Rejected by peer',
      wiType,
    });
  }

  if (transitionId === REVOKE_APPROVAL) {

    const wiType = '29000';
    const workInfoItemId = null; // We never update this, we create new ones

    const notes = window.sessionStorage.getItem('revokeNote');

    return getWIIparams({
      workInfoItemId,
      crqId,
      network,
      notes,
      summary: 'Revoked Approval for CRQ',
      wiType,
    });
  }

  if (transitionId === CANCEL_CRQ && !isEmergency) {
    const cancelledBy = loggedInUserName;
    const wiType = CANCELLATION_INFORMATION[network];

    const date = nowInNetworkTZ(network).format('MM/DD/YYYY - hh:mm');
    const cancelledData = {
      cancelledBy,
      date,
    };

    const workInfoItemId = null; // We never update this, we create new ones
    return getWIIparams({
      workInfoItemId,
      crqId,
      network,
      notes: JSON.stringify(cancelledData),
      summary: 'Crq cancelled',
      wiType,
    });
  }

  return null;
};


// method to get the work info item parameters from state
// returns a promise as the binary data for the files is read asynchronously
const getCrqWIIParamsFromFormDataAndUser = ({
  formData,
  user,
  statusTransition,
}) => {



  const crqData = formData;
  const crqId = formData.crqId ? formData.crqId : null;
  const network = currentSelectedNetwork(crqData);
  // data from auth
  const {
    roles,
  } = user;

  const wiiParams = [];

  // cachedWIIs are literally section5 modal data
  const modalData = getCachedWIIs(formData);

  const netowrkItSecurityParams = getParamsForNetowrkItSecurityAssessment({
    formData: crqData,
    crqId,
    network,
  });

  if (netowrkItSecurityParams) {
    wiiParams.push(netowrkItSecurityParams);
  }

  const customerImpactingParams = getParamsForCustomerImpacting({
    formData: crqData,
    crqId,
    network,
  });

  if (customerImpactingParams) {
    wiiParams.push(customerImpactingParams);
  }

  // we include a work info item for the responses to business justification
  const businessParams = getParamsForBusinessJustification({
    crqData,
    crqId,
    network,
    roles,
  });

  if (businessParams) {
    wiiParams.push(businessParams);
  }


  const technologyRevoverParams = getParamsForTechnologyRecoveryPlan({
    formData,
    crqId,
    network,
    crqData,
  });

  if (technologyRevoverParams) {
    wiiParams.push(technologyRevoverParams);
  }
  // Portal generated mobile CRQs need a WII for Peer group review
  const peerGroupReviewParams = getParamsForPeerGroupReviewParams({
    crqId,
    network,
    formData,
  });
  if (peerGroupReviewParams) {
    wiiParams.push(peerGroupReviewParams);
  }


  // add any WIIs as a result of the status transition
  const transitionParams = getParamsForStatusTransition({
    statusTransition,
    crqData,
    crqId,
    network,
    user,
    formData,
  });

  if (transitionParams) {
    wiiParams.push(transitionParams);
  }
  // get the params for section 5
  // this involves a promise so that the files can be read
  return getParamsFromSection5({
    modalData,
    crqId,
    network,
  }).then((section5Params) => {

    // FN: 01/06/2020
    // We need to collect up all of the WII into one request. The problem is section 5 may contains both existing WII as well
    // as new WII. This being the case we may end up accidently sending up multiple wii with the same workInfoItemId. As such,
    // we have to make sure we don't duplicate any by keeping track of the id
    const wiiIds = [];
    const params = [];

    const addWorkInfoItemsFromArray = (wiiArray) => {
      wiiArray.forEach((wii) => {
        const thisWorkInfoItemId = wii.id;

        if (thisWorkInfoItemId) {
          if (wiiIds.includes(thisWorkInfoItemId)) {
            return null;
          }
        }
        wiiIds.push(thisWorkInfoItemId);
        params.push(wii);
        return null;
      });
    };
    addWorkInfoItemsFromArray(wiiParams);

    // section5Params may contain old examples of a wii with a workInfoItemId. As such, we push them last.
    addWorkInfoItemsFromArray(section5Params);

    return params;
  });
};

const getMobileVINEWIIParams = ({
  crqId,
  network,
  user,
  formData,
}) => {
  // to pass dynamic fixed line data to mobile automated wii notes
  const riskLevel = formData.riskValue;
  const changeReasone = formData.dataByFormSectionId.CHANGE_DETAILS.dataByFieldId.change_reason.displayValues[0].text;

  const wiiParams = [];
  if (network === 'mobile') {
    mobileVINERefWIT.forEach((vineInfoType) => {
      // add any WIIs as a result of the status transition
      const mobileVINEParams = getParamsForMobileVINE({
        vineInfoType,
        // formData: crqData,
        crqId,
        network,
        user,
        riskLevel,
        changeReasone,
      });
      wiiParams.push(mobileVINEParams);
      return null;
    });
  }
  // Portal generated mobile CRQs need a WII for Peer group review for VINE
  const peerGroupReviewParams = getParamsForPeerGroupReviewParams({
    crqId,
    network,
    formData,
  });
  if (peerGroupReviewParams) {
    wiiParams.push(peerGroupReviewParams);
  }
  return Promise.all(wiiParams);
};

export {
  getCrqWIIParamsFromFormDataAndUser,
  getWIIWithAttachmentParams,
  getMobileVINEWIIParams,
};
